import { useEffect, useRef } from 'react';
import { useSendEvent } from './useEmbedEvents';

/**
 * Notifies the embedding page (via the `iframeFullScreenToggle` event) whenever a modal opens or
 * closes, so the embed can grow the iframe to a viewport-sized fullscreen layout. That layout is
 * what gives the modal a bounded height (`90dvh` resolves to the visible viewport instead of the
 * iframe's full content height), which is required for its body to scroll.
 *
 * We deliberately skip the first run while the modal is still closed. These modals mount closed,
 * and some of them (e.g. the places modal nested in the filters modal) only mount once a *parent*
 * modal opens. A spurious `fullscreen: false` from that mount would otherwise clobber the `true`
 * the parent modal just sent, collapsing the iframe back to content height so the overlay can no
 * longer scroll. The embed ref-counts these events, so balanced open/close pairs compose correctly
 * even when modals are stacked.
 */
const useFullscreenToggleEvent = (open: boolean) => {
    const sendEvent = useSendEvent();
    const hasOpened = useRef(false);

    useEffect(() => {
        if (open) {
            hasOpened.current = true;
        }

        // Never been open: don't emit a closing event that would clobber another modal's state.
        if (!open && !hasOpened.current) {
            return;
        }

        sendEvent({ type: 'iframeFullScreenToggle', fullscreen: open, source: 'travelbase' });
    }, [open, sendEvent]);
};

export default useFullscreenToggleEvent;
